import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  CheckCircle,
  LogIn,
  ShieldCheck,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { usePageTitle } from '../hooks/usePageTitle';

/**
 * Password Reset Complete Page - World-Class UI Implementation
 *
 * Features:
 * - 1-column centered layout (mobile-first design)
 * - Success confirmation
 * - shadcn/ui Design System
 * - Responsive design
 * - WCAG 2.1 AA compliant
 * - Smooth micro-interactions
 *
 * @returns Password reset complete page component
 */
const PasswordResetComplete: React.FC = () => {
  usePageTitle('パスワード変更完了');
  const navigate = useNavigate();

  const handleGoToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="max-w-[480px] mx-auto w-full">
          {/* SALONHUB Logo */}
          <img
            src="/assets/logos/salon-hub-logo.png"
            alt="SALON HUB"
            className="w-full max-w-[175px] h-auto mb-6 transition-transform duration-300 cursor-pointer hover:scale-105"
            onClick={handleGoToLogin}
          />

          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5, type: "spring", stiffness: 200 }}
          >
            <div className="flex justify-center mb-4">
              <div className="w-[100px] h-[100px] rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle className="w-16 h-16 text-primary" />
              </div>
            </div>
          </motion.div>

          {/* Title */}
          <h1 className="scroll-m-20 text-2xl font-bold tracking-tight text-gray-900 mb-2 leading-tight text-center">
            パスワード変更完了
          </h1>

          <p className="text-base text-gray-600 leading-relaxed mb-6 text-center">
            新しいパスワードが正常に設定されました。
            <br />
            今すぐログインしてご利用いただけます。
          </p>

          {/* Security Confirmation */}
          <div className="p-4 mb-6 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="text-primary w-7 h-7" />
              <h6 className="font-semibold text-gray-900 text-lg">
                セキュリティ確認
              </h6>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              アカウントのセキュリティが強化されました。
              <br />
              新しいパスワードは安全に保管されています。
            </p>
          </div>

          {/* Login Button */}
          <Button
            className="w-full min-h-[56px] py-2.5"
            onClick={handleGoToLogin}
          >
            <LogIn className="mr-2 h-4 w-4" />
            ログインページへ
          </Button>

          {/* Additional Info */}
          <p className="text-sm text-gray-500 leading-relaxed text-center mt-4">
            ログイン時に問題が発生した場合は、もう一度パスワードリセットをお試しください
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default PasswordResetComplete;
